import { cn } from "@/lib/utils";
import Pagination from "@/components/ui/Pagination";
import {
  AdminCounselorResourceService,
  type CounselorResource,
  type ResourceQueryParams,
} from "@/services/counselor-resources";
import {
  Edit2,
  Eye,
  EyeOff,
  FileText,
  GripVertical,
  Plus,
  RefreshCcw,
  Search,
  SortAsc,
  SortDesc,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";

interface QueryParams {
  page: number;
  limit: number;
  search: string;
  published?: boolean;
  sortBy: string;
  sortOrder: "ASC" | "DESC";
}

const CounselorResources: React.FC = () => {
  const [, setLocation] = useLocation();
  const [resources, setResources] = useState<CounselorResource[]>([]);
  const [totalResources, setTotalResources] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [queryParams, setQueryParams] = useState<QueryParams>({
    page: 1,
    limit: 10,
    search: "",
    sortBy: "order",
    sortOrder: "ASC",
  });

  // Load resources on component mount and when query params change
  useEffect(() => {
    loadResources();
  }, [queryParams]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const loadResources = async () => {
    setIsLoading(true);
    setError("");

    try {
      const resourceParams: ResourceQueryParams = {
        page: queryParams.page,
        limit: queryParams.limit,
        search: queryParams.search || undefined,
        published: queryParams.published,
        sortBy: queryParams.sortBy,
        sortOrder: queryParams.sortOrder,
      };

      const response = await AdminCounselorResourceService.getResources(
        resourceParams
      );

      setResources(response.resources);
      setTotalResources(response.total);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      console.error("Failed to load counselor resources:", err);
      setError("Failed to load counselor resources. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const togglePublishStatus = async (id: string, currentStatus: boolean) => {
    try {
      setError("");
      await AdminCounselorResourceService.togglePublishStatus(
        id,
        !currentStatus
      );
      setSuccessMessage(
        `Resource ${!currentStatus ? "published" : "unpublished"} successfully!`
      );
      loadResources();
    } catch (err: any) {
      console.error("Failed to toggle publish status:", err);
      setError("Failed to update publish status. Please try again.");
    }
  };

  const deleteResource = async (id: string, title: string) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${title}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setError("");
      await AdminCounselorResourceService.deleteResource(id);
      setSuccessMessage("Resource deleted successfully!");
      loadResources();
    } catch (err: any) {
      console.error("Failed to delete resource:", err);
      setError("Failed to delete resource. Please try again.");
    }
  };

  const handleSearch = (value: string) => {
    setQueryParams((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handleSort = (field: string) => {
    setQueryParams((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === "ASC" ? "DESC" : "ASC",
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({ ...prev, page }));
  };

  // Drag-to-reorder only makes sense in the default, unfiltered Order view —
  // reordering within a search result or a different sort would be
  // ambiguous about what "position" even means.
  const isDefaultOrderView =
    queryParams.sortBy === "order" &&
    queryParams.sortOrder === "ASC" &&
    !queryParams.search;

  const handleDrop = async (targetIndex: number) => {
    if (!dragId) return;
    const sourceIndex = resources.findIndex((r) => r.id === dragId);
    setDragId(null);
    if (sourceIndex === -1 || sourceIndex === targetIndex) return;

    const reordered = [...resources];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setResources(reordered); // optimistic — this page is on a single page of the default sort

    setIsReordering(true);
    setError("");
    try {
      await AdminCounselorResourceService.reorderResources(
        reordered.map((r) => r.id)
      );
      loadResources(); // picks up the real `order` values from the server
    } catch (err: any) {
      console.error("Failed to reorder resources:", err);
      setError("Failed to save the new order. Please try again.");
      loadResources(); // revert to the server's actual order
    } finally {
      setIsReordering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-gray-600">Loading counselor resources...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Counselor Resources
            </h1>
            <p className="text-gray-600">
              Create and manage resources for counselors
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadResources}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <RefreshCcw
              className={cn("h-4 w-4", isLoading && "animate-spin")}
            />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setLocation("/counselor-resources/create")}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Create Resource</span>
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search resources..."
              value={queryParams.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
      </div>

      {!isDefaultOrderView && resources.length > 0 && (
        <p className="text-xs text-gray-500">
          Drag-to-reorder is only available in the default Order view. Clear search and sorting to reorder.
        </p>
      )}

      {/* Resources Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {resources.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium">No resources found</p>
            <p className="text-sm">Create your first counselor resource to get started.</p>
          </div>
        ) : (
          <>
          <ul className="divide-y divide-gray-200 md:hidden">
            {resources.map((resource) => (
              <li key={resource.id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    #{resource.order} {resource.title}
                  </p>
                  <span
                    className={cn(
                      "shrink-0 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full",
                      resource.isPublished
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800",
                    )}
                  >
                    {resource.isPublished ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {resource.description || "No description"}
                </p>
                <p className="text-xs text-gray-400">
                  Updated {new Date(resource.updatedAt).toLocaleDateString()}
                </p>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={() => setLocation(`/counselor-resources/edit/${resource.id}`)}
                    className="text-primary hover:text-primary/80 transition-colors"
                    aria-label="Edit resource"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => togglePublishStatus(resource.id, resource.isPublished)}
                    className={cn(
                      "transition-colors",
                      resource.isPublished
                        ? "text-orange-600 hover:text-orange-700"
                        : "text-green-600 hover:text-green-700",
                    )}
                    aria-label={resource.isPublished ? "Unpublish resource" : "Publish resource"}
                  >
                    {resource.isPublished ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => deleteResource(resource.id, resource.title)}
                    className="text-red-600 hover:text-red-700 transition-colors"
                    aria-label="Delete resource"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

        <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {isDefaultOrderView && <th className="w-8 px-2 py-3" />}
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("order")}
              >
                <div className="flex items-center space-x-1">
                  <span>Order</span>
                  {queryParams.sortBy === "order" &&
                    (queryParams.sortOrder === "ASC" ? (
                      <SortAsc className="h-3 w-3" />
                    ) : (
                      <SortDesc className="h-3 w-3" />
                    ))}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("title")}
              >
                <div className="flex items-center space-x-1">
                  <span>Title</span>
                  {queryParams.sortBy === "title" &&
                    (queryParams.sortOrder === "ASC" ? (
                      <SortAsc className="h-3 w-3" />
                    ) : (
                      <SortDesc className="h-3 w-3" />
                    ))}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort("updatedAt")}
              >
                <div className="flex items-center space-x-1">
                  <span>Last Modified</span>
                  {queryParams.sortBy === "updatedAt" &&
                    (queryParams.sortOrder === "ASC" ? (
                      <SortAsc className="h-3 w-3" />
                    ) : (
                      <SortDesc className="h-3 w-3" />
                    ))}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {resources.map((resource, index) => (
                <tr
                  key={resource.id}
                  className={cn(
                    "hover:bg-gray-50",
                    dragId === resource.id && "opacity-40"
                  )}
                  draggable={isDefaultOrderView && !isReordering}
                  onDragStart={() => setDragId(resource.id)}
                  onDragOver={(e) => isDefaultOrderView && e.preventDefault()}
                  onDrop={() => isDefaultOrderView && !isReordering && handleDrop(index)}
                >
                  {isDefaultOrderView && (
                    <td className="px-2 py-4 text-gray-400 cursor-grab active:cursor-grabbing" title="Drag to reorder">
                      <GripVertical className="h-4 w-4" />
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {resource.order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {resource.title}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600 max-w-xs truncate">
                      {resource.description || "No description"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={cn(
                        "inline-flex px-2 py-1 text-xs font-semibold rounded-full",
                        resource.isPublished
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      )}
                    >
                      {resource.isPublished ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(resource.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          setLocation(
                            `/counselor-resources/edit/${resource.id}`
                          )
                        }
                        className="text-primary hover:text-primary/80 transition-colors"
                        title="Edit resource"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() =>
                          togglePublishStatus(resource.id, resource.isPublished)
                        }
                        className={cn(
                          "transition-colors",
                          resource.isPublished
                            ? "text-orange-600 hover:text-orange-700"
                            : "text-green-600 hover:text-green-700"
                        )}
                        title={
                          resource.isPublished
                            ? "Unpublish resource"
                            : "Publish resource"
                        }
                      >
                        {resource.isPublished ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() =>
                          deleteResource(resource.id, resource.title)
                        }
                        className="text-red-600 hover:text-red-700 transition-colors"
                        title="Delete resource"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        </div>
          </>
        )}

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
          <Pagination
            page={queryParams.page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            total={totalResources}
            pageSize={queryParams.limit}
            itemLabel="resources"
          />
        </div>
      </div>
    </div>
  );
};

export default CounselorResources;
